/**
 * PWA — instalación (sep-2026).
 * · Android/Chrome: NO se hace preventDefault sobre `beforeinstallprompt` ⇒ Chrome muestra su aviso nativo solo;
 *   además guardamos el evento para que nuestro botón «Instalar app» dispare el mismo prompt.
 * · iOS/Safari: no existe el evento ⇒ mostramos pasos manuales (Compartir → Agregar a pantalla de inicio).
 * · Banner: primera visita en móvil, cerrable; no vuelve en 14 días (localStorage). Nunca dentro de la app instalada.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

export const BANNER_KEY = 'lineage_pwa_banner_dismissed_at';
export const BANNER_SNOOZE_MS = 14 * 24 * 60 * 60 * 1000;
export const INSTALLED_KEY = 'lineage_pwa_installed';

const ua = () => (typeof navigator !== 'undefined' ? navigator.userAgent || '' : '');
export const isIOS = () => /iPad|iPhone|iPod/.test(ua()) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
export const isSafari = () => /Safari/.test(ua()) && !/CriOS|FxiOS|EdgiOS|OPiOS|Chrome|Android/.test(ua());
export const isAndroid = () => /Android/.test(ua());
export const isStandalone = () => (typeof window !== 'undefined')
  && (window.matchMedia?.('(display-mode: standalone)').matches || window.navigator.standalone === true
    || new URLSearchParams(window.location.search).get('source') === 'pwa');

const bannerSnoozed = () => {
  try {
    const at = Number(localStorage.getItem(BANNER_KEY) || 0);
    return at > 0 && Date.now() - at < BANNER_SNOOZE_MS;
  } catch { return false; }
};

const PwaContext = createContext({
  canPrompt: false, installed: false, standalone: false, ios: false, android: false, bannerVisible: false,
  promptInstall: async () => 'unavailable', dismissBanner: () => {}, openGuide: () => {}, closeGuide: () => {}, guideOpen: false,
});

export const PwaInstallProvider = ({ children }) => {
  const [deferred, setDeferred] = useState(null);
  const [installed, setInstalled] = useState(() => { try { return localStorage.getItem(INSTALLED_KEY) === '1'; } catch { return false; } });
  const [bannerVisible, setBannerVisible] = useState(false);
  const [guideOpen, setGuideOpen] = useState(false);
  const standalone = isStandalone();
  const ios = isIOS();
  const android = isAndroid();

  useEffect(() => {
    const onPrompt = (e) => { setDeferred(e); };  // sin preventDefault: Chrome muestra su aviso nativo
    const onInstalled = () => { setInstalled(true); setDeferred(null); setBannerVisible(false); try { localStorage.setItem(INSTALLED_KEY, '1'); } catch {} };
    window.addEventListener('beforeinstallprompt', onPrompt);
    window.addEventListener('appinstalled', onInstalled);
    return () => { window.removeEventListener('beforeinstallprompt', onPrompt); window.removeEventListener('appinstalled', onInstalled); };
  }, []);

  useEffect(() => {
    if (standalone || installed || bannerSnoozed()) return undefined;
    const mobile = window.matchMedia?.('(max-width: 768px)').matches;
    if (!mobile) return undefined;
    const t = setTimeout(() => setBannerVisible(true), 2500);
    return () => clearTimeout(t);
  }, [standalone, installed]);

  const dismissBanner = useCallback(() => {
    setBannerVisible(false);
    try { localStorage.setItem(BANNER_KEY, String(Date.now())); } catch {}
  }, []);

  const promptInstall = useCallback(async () => {
    if (!deferred) return 'unavailable';
    deferred.prompt();
    const { outcome } = await deferred.userChoice;
    if (outcome === 'accepted') { setDeferred(null); setBannerVisible(false); }
    return outcome;
  }, [deferred]);

  const value = useMemo(() => ({
    canPrompt: Boolean(deferred), installed, standalone, ios, android, bannerVisible, guideOpen,
    promptInstall, dismissBanner, openGuide: () => setGuideOpen(true), closeGuide: () => setGuideOpen(false),
  }), [deferred, installed, standalone, ios, android, bannerVisible, guideOpen, promptInstall, dismissBanner]);
  return <PwaContext.Provider value={value}>{children}</PwaContext.Provider>;
};

export const usePwaInstall = () => useContext(PwaContext);

/** Registro del service worker (red primero). Se llama una vez desde index.js. */
export const registerServiceWorker = () => {
  if (!('serviceWorker' in navigator)) return;
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js', { scope: '/' }).catch(() => {});
  });
};
